import { assembleFertLayers, type RecInputs } from "./fertility";
import type { Base, RxTunables } from "./rxInputs";
import { DEFAULT_TUNABLES, optsFor, withTunables } from "./rxInputs";
import {
  alignLayerToCells,
  clusterLabeledCells,
  smoothCategories,
  type LabeledCluster,
} from "./splitCompare";

// A cell under RX_SIMILAR_LBS of gap is “Traditional & Genius are similar”; a stage's
// contribution counts as the cause only when it is a real chunk of the gap (>=
// RX_MATERIAL_LBS and >= 20% of the total), first-in-priority-order wins.
export const RX_SIMILAR_LBS = 20;
export const RX_MATERIAL_LBS = 5;
// Level-driven causes (nutrient levels, diffusion) only attribute when the ppm gap clears these.
export const RX_LEVEL_PPM: Record<"p2o5" | "k2o", number> = {
  p2o5: 10,
  k2o: 25,
};

export interface RxComparison {
  nutrient: "p2o5" | "k2o";
  cats: (number | null)[];
  deltas: (number | null)[];
  clusters: LabeledCluster[];
  shares: { cat: number; pct: number }[];
  compared: number;
  geoms: { type: string; coordinates: unknown }[];
}

type Nutrient = "p2o5" | "k2o";
type Stage = { soil: RecInputs; t: RxTunables; alb: boolean };
type Surfaces = {
  ph: (number | null)[] | null;
  p: (number | null)[] | null;
  k: (number | null)[] | null;
  cec: (number | null)[] | null;
};

function stripTuned(s: RecInputs): RecInputs {
  return {
    ...s,
    kFixEff: undefined,
    pMinCredit: undefined,
    pRaw: undefined,
    kRaw: undefined,
    k2oAlbrecht: false,
    limeAlbrecht: false,
  };
}

function comparisonStages(input: {
  base: Base;
  nutrient: Nutrient;
  tunables: RxTunables;
  opened: RxTunables;
  albrecht: boolean;
  surfaces: Surfaces;
}): Stage[] {
  const { base, nutrient, tunables, opened, albrecht, surfaces } = input;
  const flatSeasons = opened.seasons.map((s) => ({
    crop: s.crop,
    basis: "flat" as const,
  }));
  const tFlat: RxTunables = { ...DEFAULT_TUNABLES, seasons: flatSeasons };
  const rawP = base.soil.pRaw ?? base.soil.p;
  const rawK = base.soil.kRaw ?? base.soil.k;
  const soil0 = stripTuned({
    ...base.soil,
    ph: surfaces.ph ?? base.soil.ph,
    p: surfaces.p ?? base.soil.p,
    k: surfaces.k ?? base.soil.k,
    cec: surfaces.cec ?? base.soil.cec,
  });
  const soil1 = stripTuned({ ...base.soil, p: rawP, k: rawK });
  const soil2 = {
    ...soil1,
    p: base.soil.p,
    k: base.soil.k,
    pRaw: base.soil.pRaw,
    kRaw: base.soil.kRaw,
  };
  const soil3 =
    nutrient === "p2o5"
      ? { ...soil2, pMinCredit: base.soil.pMinCredit }
      : soil2;
  const soil4 =
    nutrient === "k2o" ? { ...soil3, kFixEff: base.soil.kFixEff } : soil3;
  const t2: RxTunables = { ...tFlat, diffusionPct: tunables.diffusionPct };
  const t3: RxTunables = { ...t2, pMinPct: tunables.pMinPct };
  const t4: RxTunables = { ...t3, kFixPct: tunables.kFixPct };
  const t5: RxTunables = {
    ...t4,
    seasons: tunables.seasons,
    flatRateLbAc: tunables.flatRateLbAc,
    removalPct: tunables.removalPct,
  };
  return [
    { soil: soil0, t: tFlat, alb: false },
    { soil: soil1, t: tFlat, alb: false },
    { soil: soil2, t: t2, alb: false },
    { soil: soil3, t: t3, alb: false },
    { soil: soil4, t: t4, alb: false },
    { soil: soil4, t: t5, alb: false },
    { soil: base.soil, t: tunables, alb: albrecht },
  ];
}

function stageRates(
  stages: Stage[],
  base: Base,
  nutrient: Nutrient,
): ((number | null)[] | null)[] | null {
  const wantId = nutrient === "p2o5" ? "fert-p2o5" : "fert-k2o";
  const rates = stages.map((sg) => {
    const soil = withTunables(sg.soil, sg.t, sg.alb, nutrient, base);
    const { layers } = assembleFertLayers(
      base.geoms,
      soil,
      optsFor(nutrient, sg.t, base),
    );
    const layer = layers.find((x) => x.id === wantId) ?? layers[0];
    return layer ? alignLayerToCells(layer, base.geoms, 1) : null;
  });
  return rates.some((r) => r == null) ? null : rates;
}

function ppmGap(
  left: number | null | undefined,
  right: number | null | undefined,
  gate: number,
): boolean {
  return left != null && right != null && Math.abs(left - right) >= gate;
}

function firstMaterialCause(
  contrib: number[],
  eligible: boolean[],
  bar: number,
): number {
  for (let j = 0; j < contrib.length; j++) {
    if (eligible[j] && Math.abs(contrib[j]!) >= bar) return j + 1;
  }
  return 0;
}

function largestEligibleCause(contrib: number[], eligible: boolean[]): number {
  let best = 0;
  for (let j = 0; j < contrib.length; j++) {
    if (
      eligible[j] &&
      (best === 0 || Math.abs(contrib[j]!) > Math.abs(contrib[best - 1]!))
    )
      best = j + 1;
  }
  return best !== 0 && Math.abs(contrib[best - 1]!) >= RX_MATERIAL_LBS
    ? best
    : 0;
}

function cellCause(
  rs: (number | null)[],
  levels: {
    kriged: number | null | undefined;
    raw: number | null | undefined;
    corrected: number | null | undefined;
  },
  ppmGate: number,
): { cat: number; delta: number } | null {
  if (rs.some((v) => v == null || !Number.isFinite(v))) return null;
  const delta = rs[6]! - rs[0]!;
  if (Math.abs(delta) < RX_SIMILAR_LBS) return { cat: 0, delta };
  const eligible = [
    ppmGap(levels.kriged, levels.raw, ppmGate),
    ppmGap(levels.raw, levels.corrected, ppmGate),
    true,
    true,
    true,
    true,
  ];
  const contrib = [1, 2, 3, 4, 5, 6].map((j) => rs[j]! - rs[j - 1]!);
  const bar = Math.max(RX_MATERIAL_LBS, 0.2 * Math.abs(delta));
  let cat = firstMaterialCause(contrib, eligible, bar);
  if (cat === 0) cat = largestEligibleCause(contrib, eligible);
  return { cat, delta };
}

function rawCauseMap(
  rates: ((number | null)[] | null)[],
  stages: Stage[],
  nutrient: Nutrient,
  base: Base,
): { rawCats: (number | null)[]; deltas: (number | null)[] } {
  const n = base.geoms.length;
  const rawCats: (number | null)[] = new Array(n).fill(null);
  const deltas: (number | null)[] = new Array(n).fill(null);
  const soil0 = stages[0]!.soil;
  const rawP = base.soil.pRaw ?? base.soil.p;
  const rawK = base.soil.kRaw ?? base.soil.k;
  const lvl0 = nutrient === "p2o5" ? soil0.p : soil0.k;
  const lvlRaw = nutrient === "p2o5" ? rawP : rawK;
  const lvlCor = nutrient === "p2o5" ? base.soil.p : base.soil.k;
  const ppmGate = RX_LEVEL_PPM[nutrient];
  for (let i = 0; i < n; i++) {
    const found = cellCause(
      rates.map((r) => r![i] ?? null),
      { kriged: lvl0[i], raw: lvlRaw?.[i], corrected: lvlCor?.[i] },
      ppmGate,
    );
    if (!found) continue;
    rawCats[i] = found.cat;
    deltas[i] = found.delta;
  }
  return { rawCats, deltas };
}

function attributedDeltas(
  cats: (number | null)[],
  deltas: (number | null)[],
  rates: ((number | null)[] | null)[],
): (number | null)[] {
  const out: (number | null)[] = new Array(cats.length).fill(null);
  for (let i = 0; i < cats.length; i++) {
    const c = cats[i];
    if (c == null || deltas[i] == null) continue;
    const hi = c === 0 ? null : rates[c]?.[i];
    const lo = c === 0 ? null : rates[c - 1]?.[i];
    out[i] = c !== 0 && hi != null && lo != null ? hi - lo : deltas[i];
  }
  return out;
}

function causeShares(
  cats: (number | null)[],
  compared: number,
): { cat: number; pct: number }[] {
  return [0, 1, 2, 3, 4, 5, 6]
    .map((catId) => ({
      cat: catId,
      pct: compared
        ? (cats.filter((c) => c === catId).length / compared) * 100
        : 0,
    }))
    .filter((s) => s.pct > 0);
}

export function buildRxCauseComparison(input: {
  base: Base;
  nutrient: Nutrient;
  tunables: RxTunables;
  opened: RxTunables;
  albrecht: boolean;
  surfaces: Surfaces;
  centroids: { lng: number; lat: number }[];
}): RxComparison | null {
  const stages = comparisonStages(input);
  const rates = stageRates(stages, input.base, input.nutrient);
  if (!rates) return null;
  const { rawCats, deltas } = rawCauseMap(
    rates,
    stages,
    input.nutrient,
    input.base,
  );
  const cats = smoothCategories(rawCats, input.centroids, input.base.geoms, 2);
  const clusters = clusterLabeledCells(
    cats,
    attributedDeltas(cats, deltas, rates),
    input.centroids,
    input.base.geoms,
    false,
  );
  const compared = cats.filter((c) => c != null).length;
  return {
    nutrient: input.nutrient,
    cats,
    deltas,
    clusters,
    shares: causeShares(cats, compared),
    compared,
    geoms: input.base.geoms,
  };
}
